/**
 * 되돌릴 수 없는 일을 묻는 창 (2026-08-21).
 *
 * ★ 문구가 **일마다 다르다**. 같은 "비우기" 버튼이 받은메일함에서는 휴지통으로 옮기고
 *   휴지통에서는 영원히 지운다 — 그 차이를 여기서 말해야 한다.
 */

import { useEffect, type ReactNode } from 'react';
import { AlertTriangle } from '../../icons';
import { tMail } from '../../lib/mail-i18n';

interface MailConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** 되돌릴 수 없으면 붉게. */
  danger?: boolean;
  confirmLabel: string;
  busy?: boolean;
  /**
   * 설명과 단추 사이에 끼우는 것 (2026-08-23, 선택).
   *
   * 일괄 스팸은 "이 사람도 앞으로 막을까" 를 함께 묻는다 — 그 한 칸 때문에 창을 두 벌로 만들면
   * 한쪽만 고쳐지는 날이 온다. 안 주면 지금까지와 한 픽셀도 다르지 않다.
   */
  extra?: ReactNode;
  /**
   * 묻는 창이 아니라 **알린 뒤 닫는 창** (2026-08-25). 취소 단추를 감춘다.
   *
   * 창을 두 벌로 만들지 않으려는 것 — 결과를 알리는 창을 따로 만들면 배경 클릭·Esc·초점 같은
   * 잔손질이 두 곳으로 갈라진다(위의 `extra` 를 둔 이유와 같다).
   */
  noticeOnly?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function MailConfirmDialog(props: MailConfirmDialogProps) {
  const { open, title, description, danger, confirmLabel, busy, extra, noticeOnly, onConfirm, onCancel } = props;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="mail-confirm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="mail-confirm" role="dialog" aria-modal="true" aria-label={title}>
        <div className={`mail-confirm-icon${danger ? ' is-danger' : ''}`}>
          <AlertTriangle size={17} aria-hidden />
        </div>
        <p className="mail-confirm-title">{title}</p>
        <p className="mail-confirm-desc">{description}</p>
        {extra && <div className="mail-confirm-extra">{extra}</div>}
        <div className="mail-confirm-actions">
          {!noticeOnly && (
            <button type="button" className="mail-confirm-btn" onClick={onCancel} disabled={busy}>
              {tMail('mail.spam.cancel')}
            </button>
          )}
          <button
            type="button"
            className={`mail-confirm-btn${danger ? ' is-danger' : ' is-go'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
